import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { authenticatedFetch } from '../services/authService';

const GMAPS_LIBRARIES = ['marker'] as const;
const WARSAW_COORDS = { lat: 52.2297, lng: 21.0122 };
const BUDOWA_ICON_PX = 36;

type Budowa = {
  id: number;
  nazwa: string | null;
  telefon: string | null;
  email: string | null;
  osoba_kontaktowa: string | null;
  latitude: number | null;
  longitude: number | null;
  aktywna: boolean;
};

type Notatka = {
  id: number;
  budowa_id: number;
  data_dodania: string;
  notatka: string;
  dodane_przez: string | null;
};

interface BudowyPanelProps {
  visible: boolean;
  onClose: () => void;
  inline?: boolean;
  className?: string;
  selectedRegion?: number | null;
  selectedBudowaId?: number | null;
  onBudowaSelect?: (id: number) => void;
  editingMode?: boolean;
  onEditingModeChange?: (mode: boolean) => void;
  onGeocodeResult?: (lat: number, lng: number) => void;
  onMapClickCoordinates?: (lat: number, lng: number) => void;
  addressInput?: string;
  onAddressChange?: (address: string) => void;
  setCoordinatesInForm?: React.MutableRefObject<((lat: number, lng: number) => void) | null>;
  onBudowaSaved?: () => void;
}

// Helper to create budowa marker icon
const createBudowaIcon = (g?: any, selected: boolean = false): any | undefined => {
  try {
    if (g?.maps) {
      const size = new g.maps.Size(BUDOWA_ICON_PX, BUDOWA_ICON_PX);
      const anchor = new g.maps.Point(Math.floor(BUDOWA_ICON_PX / 2), BUDOWA_ICON_PX);
      return { 
        url: selected ? '/blue_pin_point.png' : '/blue_pin_point.png', 
        scaledSize: size, 
        anchor 
      } as any;
    }
  } catch {}
  return '/blue_pin_point.png';
};

// Map component with Google Maps
const MapCanvasComp: React.FC<{
  apiKey: string;
  mapId: string;
  center: { lat: number; lng: number };
  onLoad: (map: any) => void;
  onUnmount: () => void;
  onClick?: (e: any) => void;
}> = ({ apiKey, mapId, center, onLoad, onUnmount, onClick }) => {
  const { isLoaded, loadError } = useJsApiLoader({ 
    id: 'google-map-script', 
    googleMapsApiKey: apiKey || '', 
    version: 'weekly', 
    libraries: GMAPS_LIBRARIES as any 
  });
  
  const bounds50 = useMemo(() => {
    try {
      const dLat = 50 / 111;
      const dLon = 50 / (111 * Math.cos((center.lat * Math.PI) / 180) || 1e-6);
      return {
        north: center.lat + dLat,
        south: center.lat - dLat,
        east: center.lng + dLon,
        west: center.lng - dLon
      } as google.maps.LatLngBoundsLiteral;
    } catch { return undefined; }
  }, [center.lat, center.lng]);

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-full text-error text-sm">
        Błąd ładowania Google Maps
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-full text-muted text-sm">
        Ładowanie Google Maps...
      </div>
    );
  }

  // Validate mapId is required
  if (!mapId || !mapId.trim()) {
    return (
      <div className="flex items-center justify-center h-full text-error text-sm">
        Błąd: Map ID jest wymagane dla AdvancedMarkerElement
      </div>
    );
  }
  
  return (
    <GoogleMap
      onLoad={onLoad}
      onUnmount={onUnmount}
      onClick={onClick}
      mapContainerStyle={{ height: '100%', width: '100%', minHeight: '300px' }}
      center={center}
      zoom={13}
      options={{ 
        streetViewControl: false, 
        mapTypeControl: false, 
        restriction: bounds50 ? { latLngBounds: bounds50, strictBounds: true } : undefined, 
        mapId: mapId.trim()
      }}
    />
  );
};

const BudowyPanel: React.FC<BudowyPanelProps> = ({ 
  visible, 
  onClose, 
  inline = false, 
  className, 
  selectedRegion = null,
  selectedBudowaId: propSelectedBudowaId = null,
  onBudowaSelect,
  editingMode: propEditingMode = false,
  onEditingModeChange,
  onGeocodeResult,
  onMapClickCoordinates,
  addressInput: propAddressInput,
  onAddressChange,
  setCoordinatesInForm: propSetCoordinatesInForm,
  onBudowaSaved: propOnBudowaSaved
}) => {
  // State for budowy list
  const [activeOnly, setActiveOnly] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [list, setList] = useState<Budowa[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(propSelectedBudowaId);
  const [selected, setSelected] = useState<Budowa | null>(null);
  const [notesLoading, setNotesLoading] = useState<boolean>(false);
  const [notes, setNotes] = useState<Notatka[]>([]);
  const [newNote, setNewNote] = useState<string>('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState<string>('');
  const [editing, setEditing] = useState<boolean>(propEditingMode);
  const [form, setForm] = useState<Partial<Budowa>>({});
  const [saving, setSaving] = useState<boolean>(false);
  
  // Filters
  const [filterNazwa, setFilterNazwa] = useState<string>('');
  const [filterTelefon, setFilterTelefon] = useState<string>('');
  const [filterEmail, setFilterEmail] = useState<string>('');
  const [filterOsobaKontaktowa, setFilterOsobaKontaktowa] = useState<string>('');

  // Sync selectedId with prop
  useEffect(() => {
    if (propSelectedBudowaId !== null) {
      setSelectedId(propSelectedBudowaId);
    }
  }, [propSelectedBudowaId]);
  
  // Sync editing mode with prop
  useEffect(() => {
    setEditing(propEditingMode);
  }, [propEditingMode]);

  // Function to set coordinates in form - exposed to parent via prop
  const setCoordinatesInFormInternal = useCallback((lat: number, lng: number) => {
    if (editing) {
      setForm(prev => ({ 
        ...prev, 
        latitude: lat, 
        longitude: lng 
      }));
    }
  }, [editing]);
  
  // Expose function to parent
  useEffect(() => {
    if (propSetCoordinatesInForm) {
      // Parent can call this to set coordinates
      // We'll use the callback pattern
    }
  }, [propSetCoordinatesInForm]);
  
  // Use useEffect to expose function via callback
  const setCoordsRef = useRef<((lat: number, lng: number) => void) | null>(null);
  useEffect(() => {
    setCoordsRef.current = setCoordinatesInFormInternal;
    // Expose via prop if provided
    if (propSetCoordinatesInForm) {
      (propSetCoordinatesInForm as any).current = setCoordinatesInFormInternal;
    }
  }, [setCoordinatesInFormInternal, propSetCoordinatesInForm]);


  // Load budowy list
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await authenticatedFetch(`/api/budowa?activeOnly=${activeOnly ? 'true' : 'false'}`, { credentials: 'include' });
        if (!resp.ok) { setList([]); return; }
        const j = await resp.json();
        const arr: Budowa[] = j?.budowy || [];
        if (!cancelled) {
          setList(arr);
          // Select first item if nothing selected
          if (arr.length > 0 && selectedId === null) {
            setSelectedId(arr[0].id);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [visible, activeOnly]);

  // Load selected budowa details
  useEffect(() => {
    if (!visible) return;
    if (selectedId === null) { setSelected(null); setNotes([]); return; }
    let cancelled = false;
    (async () => {
      try {
        setNotesLoading(true);
        const [dResp, nResp] = await Promise.all([
          authenticatedFetch(`/api/budowa/${selectedId}`, { credentials: 'include' }),
          authenticatedFetch(`/api/budowa/${selectedId}/notatki`, { credentials: 'include' }),
        ]);
        if (dResp.ok) {
          const dj = await dResp.json();
          const budowa = dj?.budowa || null;
          if (!cancelled) {
            setSelected(budowa);
            // Notify parent about selection (for map sync)
            if (onBudowaSelect) {
              onBudowaSelect(budowa.id);
            }
          }
        } else {
          if (!cancelled) setSelected(null);
        }
        if (nResp.ok) {
          const nj = await nResp.json();
          if (!cancelled) setNotes(nj?.notatki || []);
        } else {
          if (!cancelled) setNotes([]);
        }
        if (!cancelled) {
          setEditing(false);
          if (onEditingModeChange) onEditingModeChange(false);
          setForm({});
          setNewNote('');
        }
      } finally {
        if (!cancelled) setNotesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [visible, selectedId]);

  // Notify parent when selectedId changes
  useEffect(() => {
    if (selectedId !== null && onBudowaSelect && selectedId > 0) {
      onBudowaSelect(selectedId);
    }
  }, [selectedId, onBudowaSelect]);

  // Filter and sort budowy list
  const filteredAndSortedList = useMemo(() => {
    let filtered = [...list];
    
    // Apply activeOnly filter
    if (activeOnly) {
      filtered = filtered.filter(b => b.aktywna === true);
    }
    
    // Apply text filters (case-insensitive)
    if (filterNazwa.trim()) {
      filtered = filtered.filter(b => 
        (b.nazwa || '').toLowerCase().includes(filterNazwa.toLowerCase())
      );
    }
    if (filterTelefon.trim()) {
      filtered = filtered.filter(b => 
        (b.telefon || '').toLowerCase().includes(filterTelefon.toLowerCase())
      );
    }
    if (filterEmail.trim()) {
      filtered = filtered.filter(b => 
        (b.email || '').toLowerCase().includes(filterEmail.toLowerCase())
      );
    }
    if (filterOsobaKontaktowa.trim()) {
      filtered = filtered.filter(b => 
        (b.osoba_kontaktowa || '').toLowerCase().includes(filterOsobaKontaktowa.toLowerCase())
      );
    }
    
    // Sort: active first, then inactive (both sorted by id)
    filtered.sort((a, b) => {
      if (a.aktywna !== b.aktywna) {
        return a.aktywna ? -1 : 1; // Active first
      }
      return (a.id || 0) - (b.id || 0); // Then by id
    });
    
    return filtered;
  }, [list, activeOnly, filterNazwa, filterTelefon, filterEmail, filterOsobaKontaktowa]);

  const startNew = () => {
    setEditing(true);
    if (onEditingModeChange) onEditingModeChange(true);
    setSelectedId(null);
    setSelected(null);
    setForm({ nazwa: '', telefon: '', email: '', osoba_kontaktowa: '', latitude: null, longitude: null, aktywna: true });
    if (onBudowaSelect) onBudowaSelect(-1); // Signal new mode
  };

  const startEdit = () => {
    if (!selected) return;
    setEditing(true);
    if (onEditingModeChange) onEditingModeChange(true);
    setForm({ ...selected });
  };

  const saveBudowa = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      const payload = {
        nazwa: form.nazwa?.trim() ?? '',
        telefon: form.telefon ?? null,
        email: form.email ?? null,
        osoba_kontaktowa: form.osoba_kontaktowa ?? null,
        latitude: form.latitude ?? null,
        longitude: form.longitude ?? null,
        aktywna: form.aktywna ?? true,
      };
      if (selectedId) {
        const r = await authenticatedFetch(`/api/budowa/${selectedId}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (r.ok) {
          const j = await r.json();
          setSelected(j.budowa);
          setList(prev => prev.map(b => b.id === selectedId ? j.budowa : b));
          setEditing(false);
          if (onEditingModeChange) onEditingModeChange(false);
          if (propOnBudowaSaved) propOnBudowaSaved();
        }
      } else {
        const r = await authenticatedFetch('/api/budowa', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (r.ok) {
          const j = await r.json();
          setList(prev => [j.budowa, ...prev]);
          setSelectedId(j.budowa?.id ?? null);
          setSelected(j.budowa ?? null);
          setEditing(false);
          if (onEditingModeChange) onEditingModeChange(false);
          if (propOnBudowaSaved) propOnBudowaSaved();
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const addNote = async (budowaId: number) => {
    if (!budowaId || !newNote.trim()) return;
    try {
      setSaving(true);
      const r = await authenticatedFetch(`/api/budowa/${budowaId}/notatki`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notatka: newNote }) });
      if (r.ok) {
        const j = await r.json();
        setNotes(prev => [j.notatka, ...prev]);
        setNewNote('');
      }
    } finally {
      setSaving(false);
    }
  };

  const saveNote = async (noteId: number) => {
    if (!selectedId) return;
    try {
      setSaving(true);
      const r = await authenticatedFetch(`/api/budowa/${selectedId}/notatki/${noteId}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notatka: editingNoteText }) });
      if (r.ok) {
        const j = await r.json();
        setNotes(prev => prev.map(n => n.id === noteId ? j.notatka : n));
        setEditingNoteId(null);
        setEditingNoteText('');
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId: number) => {
    if (!selectedId) return;
    try {
      setSaving(true);
      const r = await authenticatedFetch(`/api/budowa/${selectedId}/notatki/${noteId}`, { method: 'DELETE', credentials: 'include' });
      if (r.ok) {
        setNotes(prev => prev.filter(n => n.id !== noteId));
      }
    } finally {
      setSaving(false);
    }
  };

  const panelClass = useMemo(() => {
    if (inline) return visible ? '' : 'hidden';
    return visible ? 'fixed inset-0 z-40' : 'hidden';
  }, [visible, inline]);

  return (
    <div className={`${panelClass} ${className ?? ''}`}>
      {!inline && (<div className="absolute inset-0 bg-black/30" onClick={onClose} />)}
      <div className={`${inline ? 'h-full w-full' : 'absolute right-0 top-0 h-full w-[90vw] max-w-[1400px]'} bg-card ${inline ? '' : 'border-l'} border-border shadow-xl flex flex-col`}>
        {/* Header */}
        <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-card">
          <div className="font-semibold text-sm text-text">Budowy</div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1 text-xs text-text">
              <input 
                type="checkbox" 
                className="rounded border-border" 
                checked={activeOnly} 
                onChange={(e) => setActiveOnly(e.target.checked)} 
              />
              Tylko aktywne
            </label>
            <button 
              onClick={startNew} 
              className="h-7 px-2 rounded-md border border-border bg-card text-text hover:bg-bg text-xs"
            >
              + Budowa
            </button>
            {selected && (
              <button 
                onClick={startEdit} 
                className="h-7 px-2 rounded-md border border-border bg-card text-text hover:bg-bg text-xs"
              >
                Edytuj
              </button>
            )}
          </div>
        </div>

        {/* Main content: Filters and List */}
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Filters section */}
          <div className="px-4 py-2 bg-card border-b border-border space-y-2">
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-1 text-xs text-text">
                <input 
                  type="checkbox" 
                  className="rounded border-border" 
                  checked={activeOnly} 
                  onChange={(e) => setActiveOnly(e.target.checked)} 
                />
                Tylko aktywne
              </label>
              <div className="flex items-center gap-2">
                <label className="text-xs text-text">Nazwa:</label>
                <input
                  type="text"
                  value={filterNazwa}
                  onChange={(e) => setFilterNazwa(e.target.value)}
                  placeholder="Filtr..."
                  className="w-32 px-2 py-1 text-xs border border-border rounded bg-card text-text placeholder:text-muted"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-text">Telefon:</label>
                <input
                  type="text"
                  value={filterTelefon}
                  onChange={(e) => setFilterTelefon(e.target.value)}
                  placeholder="Filtr..."
                  className="w-32 px-2 py-1 text-xs border border-border rounded bg-card text-text placeholder:text-muted"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-text">Email:</label>
                <input
                  type="text"
                  value={filterEmail}
                  onChange={(e) => setFilterEmail(e.target.value)}
                  placeholder="Filtr..."
                  className="w-32 px-2 py-1 text-xs border border-border rounded bg-card text-text placeholder:text-muted"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-text">Osoba kontaktowa:</label>
                <input
                  type="text"
                  value={filterOsobaKontaktowa}
                  onChange={(e) => setFilterOsobaKontaktowa(e.target.value)}
                  placeholder="Filtr..."
                  className="w-32 px-2 py-1 text-xs border border-border rounded bg-card text-text placeholder:text-muted"
                />
              </div>
            </div>
          </div>

          {/* List section */}
          <div className="flex-1 overflow-y-auto border-t border-border bg-card">
            {loading ? (
              <div className="p-3 text-xs text-muted">Ładowanie...</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] text-muted border-b border-border bg-bg sticky top-0">
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2">Nazwa</th>
                    <th className="px-3 py-2">Telefon</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Osoba kontaktowa</th>
                    <th className="px-3 py-2">Lokalizacja</th>
                    <th className="px-3 py-2">Aktywna</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {editing && selectedId === null && (
                    <tr className="bg-accent/10">
                      <td></td>
                      <td className="px-3 py-2">
                        <input 
                          autoFocus 
                          value={form.nazwa ?? ''} 
                          onChange={(e) => setForm(prev => ({ ...prev, nazwa: e.target.value }))} 
                          className="w-full text-sm px-2 py-1 border border-border rounded bg-card text-text" 
                          placeholder="Nazwa" 
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input 
                          value={form.telefon ?? ''} 
                          onChange={(e) => setForm(prev => ({ ...prev, telefon: e.target.value }))} 
                          className="w-full text-sm px-2 py-1 border border-border rounded bg-card text-text" 
                          placeholder="Telefon" 
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input 
                          value={form.email ?? ''} 
                          onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))} 
                          className="w-full text-sm px-2 py-1 border border-border rounded bg-card text-text" 
                          placeholder="Email" 
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input 
                          value={form.osoba_kontaktowa ?? ''} 
                          onChange={(e) => setForm(prev => ({ ...prev, osoba_kontaktowa: e.target.value }))} 
                          className="w-full text-sm px-2 py-1 border border-border rounded bg-card text-text" 
                          placeholder="Osoba kontaktowa" 
                        />
                      </td>
                      <td className="px-3 py-2 text-xs text-muted">
                        {form.latitude && form.longitude ? (
                          `${form.latitude.toFixed(6)}, ${form.longitude.toFixed(6)}`
                        ) : (
                          <span className="text-error">Brak współrzędnych</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <label className="inline-flex items-center gap-2 text-xs text-text">
                          <input 
                            type="checkbox" 
                            className="rounded border-border" 
                            checked={!!form.aktywna} 
                            onChange={(e) => setForm(prev => ({ ...prev, aktywna: e.target.checked }))} 
                          /> 
                          Aktywna
                        </label>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button 
                          disabled={saving || !form.nazwa?.trim()} 
                          onClick={saveBudowa} 
                          className="h-7 px-2 rounded-md bg-accent text-white text-xs hover:bg-accent/90 disabled:opacity-50"
                        >
                          Zapisz
                        </button>
                        <button 
                          disabled={saving} 
                          onClick={() => { 
                            setEditing(false); 
                            if (onEditingModeChange) onEditingModeChange(false);
                            setForm({}); 
                          }} 
                          className="ml-1 h-7 px-2 rounded-md bg-card hover:bg-bg text-text border border-border text-xs disabled:opacity-50"
                        >
                          Anuluj
                        </button>
                      </td>
                    </tr>
                  )}
                  {filteredAndSortedList.map((b) => {
                    const isOpen = expanded.has(b.id);
                    const hasLocation = b.latitude !== null && b.longitude !== null;
                    return (
                      <React.Fragment key={b.id}>
                        <tr 
                          className={`${selectedId === b.id ? 'bg-accent/10' : 'hover:bg-bg'} cursor-pointer ${!b.aktywna ? 'opacity-50' : ''}`}
                          onClick={() => setSelectedId(b.id)}
                        >
                          <td className="px-3 py-2">
                            <button 
                              onClick={(e) => { 
                                e.stopPropagation();
                                const s = new Set(expanded); 
                                if (s.has(b.id)) s.delete(b.id); 
                                else s.add(b.id); 
                                setExpanded(s); 
                                if (selectedId !== b.id) setSelectedId(b.id); 
                              }} 
                              className="h-6 w-6 rounded border border-border bg-card text-text text-xs hover:bg-bg"
                            >
                              {isOpen ? '-' : '+'}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-text">{b.nazwa || '(bez nazwy)'}</td>
                          <td className="px-3 py-2 text-text">{b.telefon || ''}</td>
                          <td className="px-3 py-2 text-text">{b.email || ''}</td>
                          <td className="px-3 py-2 text-text">{b.osoba_kontaktowa || ''}</td>
                          <td className="px-3 py-2">
                            {hasLocation ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] bg-ok/20 text-ok border border-ok/30">
                                ✓ {b.latitude?.toFixed(4)}, {b.longitude?.toFixed(4)}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-error/20 text-error border border-error/30">
                                <span>⚠</span> Brak współrzędnych
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] ${b.aktywna ? 'bg-ok/20 text-ok border border-ok/30' : 'bg-muted/20 text-muted border border-muted/30'}`}>
                              {b.aktywna ? 'Tak' : 'Nie'}
                            </span>
                          </td>
                        </tr>
                        {isOpen && selectedId === b.id && (
                          <tr>
                            <td></td>
                            <td colSpan={7} className="px-3 py-2 bg-bg">
                              {notesLoading ? (
                                <div className="text-xs text-muted">Ładowanie...</div>
                              ) : (
                                <div className="flex flex-col gap-3">
                                  <div className="flex items-start gap-2">
                                    <textarea 
                                      rows={3} 
                                      value={newNote} 
                                      onChange={(e) => setNewNote(e.target.value)} 
                                      placeholder="Dodaj notatkę" 
                                      className="flex-1 text-sm px-2 py-1 border border-border rounded bg-card text-text placeholder:text-muted" 
                                    />
                                    <button 
                                      disabled={saving || !newNote.trim()} 
                                      onClick={() => addNote(b.id)} 
                                      className="h-8 px-2 rounded-md bg-accent text-white text-xs hover:bg-accent/90 disabled:opacity-50" 
                                      title="Dodaj notatkę"
                                    >
                                      +
                                    </button>
                                  </div>
                                  <div className="space-y-2">
                                    {notes.map(n => (
                                      <div key={n.id} className="flex items-start gap-2 p-2 bg-card border border-border rounded">
                                        <div className="flex-1">
                                          <div className="text-[11px] text-muted mb-1">
                                            {new Date(n.data_dodania).toLocaleString('pl-PL')} {n.dodane_przez && `przez ${n.dodane_przez}`}
                                          </div>
                                          {editingNoteId === n.id ? (
                                            <div className="flex items-start gap-2">
                                              <textarea 
                                                rows={3} 
                                                value={editingNoteText} 
                                                onChange={(e) => setEditingNoteText(e.target.value)} 
                                                className="flex-1 text-sm px-2 py-1 border border-border rounded bg-card text-text" 
                                              />
                                              <button 
                                                disabled={saving || !editingNoteText.trim()} 
                                                onClick={() => saveNote(n.id)} 
                                                className="h-8 px-2 rounded-md bg-accent text-white text-xs hover:bg-accent/90 disabled:opacity-50"
                                              >
                                                Zapisz
                                              </button>
                                              <button 
                                                disabled={saving} 
                                                onClick={() => { setEditingNoteId(null); setEditingNoteText(''); }} 
                                                className="h-8 px-2 rounded-md bg-card hover:bg-bg text-text border border-border text-xs disabled:opacity-50"
                                              >
                                                Anuluj
                                              </button>
                                            </div>
                                          ) : (
                                            <div 
                                              onDoubleClick={() => { setEditingNoteId(n.id); setEditingNoteText(n.notatka); }} 
                                              className="text-sm whitespace-pre-wrap cursor-text text-text"
                                            >
                                              {n.notatka}
                                            </div>
                                          )}
                                        </div>
                                        <button 
                                          disabled={saving} 
                                          onClick={() => deleteNote(n.id)} 
                                          className="h-7 px-2 rounded-md border border-border bg-card text-error hover:bg-error/10 text-xs" 
                                          title="Usuń notatkę"
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ))}
                                    {notes.length === 0 && (
                                      <div className="text-xs text-muted p-2">Brak notatek</div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {list.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-2 text-xs text-muted">Brak danych</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudowyPanel;
